<template>
  <div class="ur-settings ur-projects">
    <!-- Header（返回鍵 + 標題 + 新增建案，設計稿）-->
    <div class="ur-settings-header ur-settings-topbar">
      <button class="ur-settings-back" :title="$t('URBANRENEWAL_ADMIN.PROJECT.BACK')" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.21917 12.6666L11.4144 18.8619L10.4716 19.8047L3.33356 12.6666L3.27637 12.6666L3.27637 12.6094L2.66689 12L3.27637 11.3905L3.27637 11.3333L3.33356 11.3333L10.4716 4.19521L11.4144 5.13802L5.21918 11.3333L19.2764 11.3333L19.2764 12.6666L5.21917 12.6666Z" fill="currentColor"></path>
        </svg>
      </button>
      <span class="ur-settings-title">{{ $t('URBANRENEWAL_ADMIN.PROJECT.TITLE') }}</span>
      <button class="ur-projects-add-btn" @click="addProject">
        <!-- PlusIcon -->
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        {{ $t('URBANRENEWAL_ADMIN.PROJECT.ADD') }}
      </button>
    </div>

    <!-- Error message -->
    <div v-if="errorMsg" class="ur-settings-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-settings-error-close" @click="errorMsg = ''">&times;</button>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && rows.length === 0" class="ur-projects-empty">
      {{ $t('URBANRENEWAL_ADMIN.NO_PROJECTS') }}
    </div>

    <!-- 建案卡片列表（設計稿：過期沉底、灰卡）-->
    <div class="ur-projects-list">
      <div
        v-for="row in sortedRows"
        :key="row._key"
        class="ur-project-card"
        :class="{ 'is-expired': isExpired(row) }"
      >
        <!-- 卡片頂列：已結束 / 已儲存 / 儲存 / 刪除 -->
        <div class="ur-project-card-head">
          <span v-if="isExpired(row)" class="ur-project-expired-tag">
            {{ $t('URBANRENEWAL_ADMIN.PROJECT.EXPIRED') }}
          </span>
          <span v-if="row.id !== null && savedIds.indexOf(row.id) !== -1" class="ur-project-saved-badge">
            {{ $t('URBANRENEWAL_ADMIN.PROJECT.SAVED') }}
          </span>
          <button
            class="ur-project-save-btn"
            :disabled="!canSaveRow(row)"
            @click="saveRow(row)"
          >
            {{ $t('URBANRENEWAL_ADMIN.PROJECT.SAVE') }}
          </button>
          <button class="ur-project-del-btn" :title="$t('URBANRENEWAL_ADMIN.PROJECT.DELETE')" @click="askDelete(row)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          </button>
        </div>

        <!-- 欄位 grid -->
        <div class="ur-project-fields">
          <div class="ur-project-field">
            <label class="ur-project-field-label">{{ $t('URBANRENEWAL_ADMIN.PROJECT.NAME') }}</label>
            <input class="ur-project-input" type="text" v-model="row.name" :placeholder="$t('URBANRENEWAL_ADMIN.PROJECT.NAME')" />
          </div>
          <div class="ur-project-field">
            <label class="ur-project-field-label">{{ $t('URBANRENEWAL_ADMIN.PROJECT.START') }}</label>
            <input class="ur-project-input" type="date" v-model="row.selection_start" />
          </div>
          <div class="ur-project-field">
            <label class="ur-project-field-label">{{ $t('URBANRENEWAL_ADMIN.PROJECT.DEADLINE') }}</label>
            <input class="ur-project-input" type="date" v-model="row.selection_deadline" />
          </div>
        </div>
      </div>
    </div>

    <!-- 刪除確認 Dialog -->
    <v-dialog v-model="deleteDialog.visible" max-width="420" @click:outside="closeDeleteDialog">
      <v-card>
        <!-- 未被擋下：確認刪除 -->
        <template v-if="!deleteDialog.blocked">
          <div class="ur-settings-dialog-body">
            <p class="ur-settings-dialog-msg">
              {{ deleteDialog.project ? $t('URBANRENEWAL_ADMIN.PROJECT.DELETE_CONFIRM', { name: deleteDialog.project.name }) : '' }}
            </p>
          </div>
          <div class="ur-settings-dialog-actions">
            <button class="ur-settings-dialog-cancel-btn" @click="closeDeleteDialog">
              {{ $t('URBANRENEWAL_ADMIN.PROJECT.CANCEL') }}
            </button>
            <button class="ur-settings-dialog-confirm-btn" @click="confirmDelete">
              {{ $t('URBANRENEWAL_ADMIN.PROJECT.DELETE') }}
            </button>
          </div>
        </template>

        <!-- 被擋下（409）：有關聯資料 -->
        <template v-else>
          <div class="ur-settings-dialog-body">
            <p class="ur-settings-dialog-msg">{{ $t('URBANRENEWAL_ADMIN.PROJECT.DELETE_HAS_DATA') }}</p>
          </div>
          <div class="ur-settings-dialog-actions">
            <button class="ur-settings-dialog-close-btn" @click="closeDeleteDialog">
              {{ $t('URBANRENEWAL_ADMIN.PROJECT.CLOSE') }}
            </button>
          </div>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';

export default {
  name: 'UrbanRenewalAdminProjectSettings',

  data() {
    return {
      rows: [],
      savedIds: [],
      loading: false,
      keySeq: 0,
      deleteDialog: {
        visible: false,
        project: null,
        blocked: false,
      },
      errorMsg: '',
    };
  },

  computed: {
    // 過期建案（選配截止日 < 今天）沉到最下面，比照設計稿
    sortedRows() {
      return this.rows.slice().sort((a, b) => (this.isExpired(a) ? 1 : 0) - (this.isExpired(b) ? 1 : 0));
    },
  },

  mounted() {
    this.loadRows();
  },

  methods: {
    newKey() {
      this.keySeq += 1;
      return this.keySeq;
    },

    goBack() {
      this.$router.back();
    },

    isExpired(row) {
      if (!row.selection_deadline) return false;
      const today = new Date().toISOString().slice(0, 10);
      return row.selection_deadline < today;
    },

    isDirty(row) {
      return (
        !row.id ||
        row.name !== row._orig.name ||
        row.selection_start !== row._orig.selection_start ||
        row.selection_deadline !== row._orig.selection_deadline
      );
    },

    canSaveRow(row) {
      return this.isDirty(row) && !!(row.name && row.name.trim());
    },

    async loadRows() {
      this.loading = true;
      try {
        await this.$store.dispatch('urbanRenewalAdmin/fetchProjects');
        const projects = this.$store.state.urbanRenewalAdmin.projects || [];
        this.rows = projects.map(p => ({
          _key: this.newKey(),
          id: p.id,
          name: p.name,
          selection_start: p.selection_start || null,
          selection_deadline: p.selection_deadline || null,
          _orig: {
            name: p.name,
            selection_start: p.selection_start || null,
            selection_deadline: p.selection_deadline || null,
          },
        }));
      } finally {
        this.loading = false;
      }
    },

    addProject() {
      this.rows.unshift({
        _key: this.newKey(),
        id: null,
        name: '',
        selection_start: null,
        selection_deadline: null,
        _orig: { name: '', selection_start: null, selection_deadline: null },
      });
    },

    async saveRow(row) {
      if (!row.name || !row.name.trim()) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.PROJECT.NAME_REQUIRED');
        return;
      }
      this.errorMsg = '';
      const payload = {
        name: row.name.trim(),
        selection_start: row.selection_start || null,
        selection_deadline: row.selection_deadline || null,
      };
      try {
        let savedId = row.id;
        if (row.id) {
          await UrbanRenewalDataService.updateProject(row.id, payload);
        } else {
          const res = await UrbanRenewalDataService.createProject(payload);
          savedId = res.data && res.data.id ? res.data.id : null;
        }
        await this.loadRows();
        if (savedId !== null) {
          this.savedIds.push(savedId);
          setTimeout(() => {
            const idx = this.savedIds.indexOf(savedId);
            if (idx !== -1) {
              this.savedIds.splice(idx, 1);
            }
          }, 2500);
        }
      } catch (_) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.PROJECT.SAVE_FAILED');
      }
    },

    askDelete(row) {
      if (row.id === null) {
        this.rows = this.rows.filter(r => r !== row);
        return;
      }
      this.deleteDialog = { visible: true, project: row, blocked: false };
    },

    async confirmDelete() {
      if (!this.deleteDialog.project) return;
      try {
        await UrbanRenewalDataService.deleteProject(this.deleteDialog.project.id);
        this.deleteDialog.visible = false;
        await this.loadRows();
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.deleteDialog.blocked = true;
        } else {
          this.closeDeleteDialog();
          this.errorMsg = this.$t('URBANRENEWAL_ADMIN.PROJECT.SAVE_FAILED');
        }
      }
    },

    closeDeleteDialog() {
      this.deleteDialog = { visible: false, project: null, blocked: false };
    },
  },
};
</script>
